/**
 * Voice Agents (ElevenLabs) — hook con query supabase DIRETTE (Task 16).
 *
 * Split da hooks.ts per disciplina file-size (M-guideline ~250 righe). Questi hook
 * NON passano da core: leggono tabelle tenant-scoped con RLS gia' in essere
 * (pattern dominante CRM per le liste read-only). Re-esportati da ./hooks.
 *
 * Tutti degradano graceful (catch → setError, stato vuoto) cosi' la UI non rompe
 * se lo schema/tabella non e' ancora migrato ("backend in deploy").
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase';
import { useUserData, useAuth } from '../auth';

// ─── useHitlPending: run in attesa di approvazione dial ─────────────────────

export interface VoiceHitlRun {
  id: string;
  organization_id: string;
  agent_id: string;
  binding_id: string;
  trigger_type: string;
  status: string;
  started_at: string;
  acting_as_user_id: string | null;
  agent_snapshot: { name?: string; icon?: string } | null;
  metadata: Record<string, unknown> | null;
  /** Arricchimenti display (lookup separati): chi viene chiamato / chi ha richiesto. */
  to_number: string | null;
  lead_name: string | null;
  agent_name: string | null;
  requested_by: string | null;
}

const HITL_COLS =
  'id, organization_id, agent_id, binding_id, trigger_type, status, started_at, acting_as_user_id, agent_snapshot, metadata';

/** Intervallo di polling: e' la GARANZIA, la subscription realtime e' best-effort. */
const HITL_POLL_MS = 30_000;

/**
 * Run voice in `awaiting_dial_approval` per l'org corrente.
 *
 * DUE meccanismi di aggiornamento, entrambi attivi:
 *  1. subscription realtime su ai_agent_runs → refetch (BEST-EFFORT);
 *  2. polling ogni 30s → refetch (GARANZIA).
 *
 * PERCHE' il polling e' obbligatorio: ai_agent_runs e' partizionata BY RANGE
 * (created_at) con publish_via_partition_root; l'efficacia di Supabase Realtime su
 * tabelle partizionate NON e' garantita. Il polling copre i buchi della subscription.
 * Entrambi vengono ripuliti su unmount.
 */
export function useHitlPending(): {
  runs: VoiceHitlRun[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  const { organizationId } = useUserData();
  const [runs, setRuns] = useState<VoiceHitlRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!organizationId) return;
    setError(null);
    const { data, error: err } = await supabase
      .from('ai_agent_runs')
      .select(HITL_COLS)
      .eq('organization_id', organizationId)
      .eq('status', 'awaiting_dial_approval')
      .order('started_at', { ascending: true });
    if (err) {
      setError(err.message);
      setRuns([]);
      setLoading(false);
      return;
    }

    // Arricchimento display: la riga HITL deve dire CHI viene chiamato (numero +
    // nome lead), con QUALE agente e CHI ha richiesto la chiamata — mai il
    // trigger_type raw. Lookup batch (1 query per tabella, org-scoped via RLS).
    type RawRun = Omit<VoiceHitlRun, 'to_number' | 'lead_name' | 'agent_name' | 'requested_by'>;
    const raw = (data ?? []) as RawRun[];
    const leadIds = [...new Set(raw.map((r) => r.metadata?.['lead_id']).filter((v): v is string => typeof v === 'string'))];
    const userIds = [...new Set(raw.map((r) => r.acting_as_user_id).filter((v): v is string => typeof v === 'string'))];
    const agentIds = [...new Set(raw.map((r) => r.agent_id))];

    const [leadsRes, usersRes, agentsRes] = await Promise.all([
      leadIds.length
        ? supabase.from('clients').select('id, business_name').in('id', leadIds)
        : Promise.resolve({ data: [] as Array<{ id: string; business_name: string }> }),
      userIds.length
        ? supabase.from('user_profiles').select('id, full_name, email').in('id', userIds)
        : Promise.resolve({ data: [] as Array<{ id: string; full_name: string | null; email: string | null }> }),
      agentIds.length
        ? supabase.from('ai_agents').select('id, name').in('id', agentIds)
        : Promise.resolve({ data: [] as Array<{ id: string; name: string }> }),
    ]);
    const leadName = new Map((leadsRes.data ?? []).map((c) => [c.id, c.business_name]));
    const userName = new Map(
      (usersRes.data ?? []).map((u) => [u.id, u.full_name || u.email || '']),
    );
    const agentName = new Map((agentsRes.data ?? []).map((a) => [a.id, a.name]));

    setRuns(
      raw.map((r) => {
        const leadId = typeof r.metadata?.['lead_id'] === 'string' ? (r.metadata['lead_id'] as string) : null;
        const toNumber =
          typeof r.metadata?.['dial_to_number'] === 'string' ? (r.metadata['dial_to_number'] as string) : null;
        return {
          ...r,
          to_number: toNumber,
          lead_name: leadId ? (leadName.get(leadId) ?? null) : null,
          agent_name: agentName.get(r.agent_id) ?? null,
          requested_by: r.acting_as_user_id ? (userName.get(r.acting_as_user_id) ?? null) : null,
        };
      }),
    );
    setLoading(false);
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId) return undefined;
    void refetch();

    // (1) Subscription best-effort.
    const channel = supabase
      .channel(`voice-hitl-pending-${organizationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ai_agent_runs',
          filter: `organization_id=eq.${organizationId}`,
        },
        () => void refetch(),
      )
      .subscribe();

    // (2) Polling garanzia 30s.
    const interval = window.setInterval(() => void refetch(), HITL_POLL_MS);

    return () => {
      void supabase.removeChannel(channel);
      window.clearInterval(interval);
    };
  }, [organizationId, refetch]);

  return { runs, loading, error, refetch };
}

// ─── useVoiceCalls: registrazioni chiamate voice ────────────────────────────

export interface VoiceCall {
  id: string;
  organization_id: string;
  status: string;
  meeting_title: string | null;
  lead_id: string | null;
  client_id: string | null;
  deal_id: string | null;
  duration_seconds: number | null;
  full_transcript: string | null;
  ai_summary: string | null;
  elevenlabs_conversation_id: string | null;
  elevenlabs_agent_id: string | null;
  voice_cost_credits: number | null;
  voice_cost_fiat_usd: number | null;
  voice_tools_invoked: Array<Record<string, unknown>> | null;
  voice_hitl_run_id: string | null;
  voice_disclaimer_played: boolean | null;
  is_test: boolean;
  created_at: string;
}

const CALL_COLS =
  'id, organization_id, status, meeting_title, lead_id, client_id, deal_id, duration_seconds, ' +
  'full_transcript, ai_summary, elevenlabs_conversation_id, elevenlabs_agent_id, voice_cost_credits, ' +
  'voice_cost_fiat_usd, voice_tools_invoked, voice_hitl_run_id, voice_disclaimer_played, is_test, created_at';

export function useVoiceCalls(): {
  calls: VoiceCall[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  const { organizationId } = useUserData();
  const [calls, setCalls] = useState<VoiceCall[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!organizationId) return;
    setError(null);
    const { data, error: err } = await supabase
      .from('meeting_recordings')
      .select(CALL_COLS)
      .eq('organization_id', organizationId)
      .eq('source', 'elevenlabs')
      .is('deleted_at', null)
      .order('created_at', { ascending: false });
    if (err) {
      setError(err.message);
      setCalls([]);
    } else {
      setCalls((data ?? []) as VoiceCall[]);
    }
    setLoading(false);
  }, [organizationId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { calls, loading, error, refetch };
}

// ─── useVoiceTos: accettazione Termini di servizio Voice (read + insert) ────

export interface VoiceTosAcceptance {
  organization_id: string;
  accepted_by: string;
  accepted_at: string;
  tos_version: string;
}

export function useVoiceTos(): {
  acceptance: VoiceTosAcceptance | null;
  accepted: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  accept: (tosVersion: string) => Promise<VoiceTosAcceptance | null>;
} {
  const { organizationId } = useUserData();
  const { user } = useAuth();
  const [acceptance, setAcceptance] = useState<VoiceTosAcceptance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!organizationId) return;
    setError(null);
    const { data, error: err } = await supabase
      .from('org_voice_terms_acceptance')
      .select('organization_id, accepted_by, accepted_at, tos_version')
      .eq('organization_id', organizationId)
      .maybeSingle();
    if (err) {
      setError(err.message);
      setAcceptance(null);
    } else {
      setAcceptance((data ?? null) as VoiceTosAcceptance | null);
    }
    setLoading(false);
  }, [organizationId]);

  const accept = useCallback(
    async (tosVersion: string) => {
      if (!organizationId || !user?.id) {
        setError('Sessione non valida');
        return null;
      }
      setError(null);
      const { data, error: err } = await supabase
        .from('org_voice_terms_acceptance')
        .insert({ organization_id: organizationId, accepted_by: user.id, tos_version: tosVersion })
        .select('organization_id, accepted_by, accepted_at, tos_version')
        .single();
      if (err) {
        setError(err.message);
        return null;
      }
      const row = data as VoiceTosAcceptance;
      setAcceptance(row);
      return row;
    },
    [organizationId, user?.id],
  );

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { acceptance, accepted: acceptance !== null, loading, error, refetch, accept };
}

// ─── useVoiceAgents: agenti con agent_type='voice' ──────────────────────────

export interface VoiceAgent {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  icon: string | null;
  agent_type: string;
  is_active: boolean;
  version: number;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

const VOICE_AGENT_COLS =
  'id, organization_id, name, description, icon, agent_type, is_active, version, metadata, created_at';

export function useVoiceAgents(): {
  agents: VoiceAgent[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  const { organizationId } = useUserData();
  const [agents, setAgents] = useState<VoiceAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Evita setState su unmount durante un refetch lento.
  const mounted = useRef(true);

  const refetch = useCallback(async () => {
    if (!organizationId) return;
    setError(null);
    const { data, error: err } = await supabase
      .from('ai_agents')
      .select(VOICE_AGENT_COLS)
      .eq('organization_id', organizationId)
      .eq('agent_type', 'voice')
      .is('deleted_at', null)
      .order('created_at', { ascending: false });
    if (!mounted.current) return;
    if (err) {
      setError(err.message);
      setAgents([]);
    } else {
      setAgents((data ?? []) as VoiceAgent[]);
    }
    setLoading(false);
  }, [organizationId]);

  useEffect(() => {
    mounted.current = true;
    void refetch();
    return () => {
      mounted.current = false;
    };
  }, [refetch]);

  return { agents, loading, error, refetch };
}

// ─── useVoiceTemplates: template marketplace agent_type='voice' ─────────────

export interface VoiceTemplate {
  id: string;
  key: string;
  label: { it?: string; en?: string; [k: string]: string | undefined };
  description: { it?: string; en?: string; [k: string]: string | undefined };
  one_liner: { it?: string; en?: string; [k: string]: string | undefined } | null;
  icon: string | null;
  is_featured: boolean;
  popularity: number;
  disclaimer_verified: boolean;
  purpose: string | null;
}

/**
 * Template vocali globali del marketplace (RLS: leggibili da tutti gli
 * authenticated, non sono tenant-scoped). Select colonne esplicite: agent_preset
 * NON serve al picker (la create ElevenLabs e' server-side via installVoiceTemplate).
 */
const VOICE_TEMPLATE_COLS =
  'id, key, label, description, one_liner, icon, is_featured, popularity, disclaimer_verified, purpose';

export function useVoiceTemplates(): {
  templates: VoiceTemplate[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  const [templates, setTemplates] = useState<VoiceTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setError(null);
    const { data, error: err } = await supabase
      .from('ai_agent_templates')
      .select(VOICE_TEMPLATE_COLS)
      .eq('agent_type', 'voice')
      .eq('is_active', true)
      .order('is_featured', { ascending: false })
      .order('popularity', { ascending: false });
    if (err) {
      setError(err.message);
      setTemplates([]);
    } else {
      setTemplates((data ?? []) as VoiceTemplate[]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { templates, loading, error, refetch };
}
